import { FunctionComponent, useCallback, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@store/store";
import {
  selectDirections,
  selectNavigationDirectionIndex,
  setNavigationDirectionIndex,
} from "@store/planRouteReducer";
import { StartPoint } from "@domain/StartPoint";
import { PlanItem } from "@dto/PlanItem";
import { t } from "i18next";
import { DirectionsList } from "./DirectionsList";
import { DirectionListHeader } from "./DirectionListHeader";
import { useNavigate } from "@hooks/useNavigate/useNavigate";

import "./PlanNavigate.css";

const getStopName = (items: (StartPoint | PlanItem)[]) => {
  const first = items[0];
  if (first.type === "StartPoint") {
    return first.name ?? first.location.address ?? t("plan.startPoint");
  } else {
    return first.plannable.name;
  }
};

const PlanNavigate: FunctionComponent = () => {
  const navigationDirectionIndex = useAppSelector(
    selectNavigationDirectionIndex,
  );
  const directions = useAppSelector(selectDirections)!;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const directionsForStop =
    navigationDirectionIndex < directions?.length
      ? directions[navigationDirectionIndex]
      : directions[0];

  useEffect(() => {
    return () => {
      // reset navigation index when exiting.
      dispatch(setNavigationDirectionIndex(0));
    };
  }, [dispatch]);

  const handleSetStopPress = useCallback(
    (direction: number) => {
      dispatch(
        setNavigationDirectionIndex(navigationDirectionIndex + direction),
      );
    },
    [dispatch, navigationDirectionIndex],
  );

  const handleEndRoutePress = useCallback(() => {
    navigate("/plan");
  }, [navigate]);

  if (!directions) {
    return null;
  }

  return (
    <div className="plan-navigate-component">
      <DirectionListHeader
        currentStopIndex={navigationDirectionIndex}
        currentStopName={getStopName(directionsForStop.end.items) ?? ""}
        distanceInMeters={directionsForStop.distanceInMeters}
        onSetStopDirections={handleSetStopPress}
        stopsCount={directions.length}
        timeInSeconds={directionsForStop.timeInSeconds}
        onEndRoutePress={handleEndRoutePress}
      />
      <div className="plan-navigate-directions-list-container">
        <DirectionsList
          steps={directionsForStop.steps}
          stopAddress={directionsForStop.end.location?.address ?? ""}
          stopName={getStopName(directionsForStop.end.items) ?? ""}
        />
      </div>
    </div>
  );
};

export { PlanNavigate };
